/**
 * A spreadsheet formula's cell references, read by code (step 1.1 of
 * docs/plans/workbook-map.md). A formula is never run: these only say what it
 * reads, so a column's formula can be written once for every row (`H2+I2` on
 * row 2 is `H+I`), and the workbook map can tell a row's formula from a
 * summary's total.
 *
 * Text in double quotes is never a reference (`TEXT(B2,"MMM-YY")` reads B).
 * A function name is never one either: a reference is one to three capital
 * letters, maybe a row, not followed by `(`, a letter or a digit.
 */

/** One reference in a formula, and where it sits in the text. */
export type SheetRef = {
  /** The sheet it names (`Data` of `Data!J2`), unquoted; absent for the formula's own sheet. */
  readonly sheet?: string;
  /** The column letters, capitals (`J`). */
  readonly column: string;
  /** The row, when the reference names one (`J2`); absent for a whole column (`J:J`) or a row-free pattern (`J`). */
  readonly row?: number;
  /** True when the row is fixed with `$` (`J$2`). */
  readonly fixedRow: boolean;
  /** A range's far end (`J300` of `J2:J300`, `J` of `J:J`). */
  readonly to?: { readonly column: string; readonly row?: number };
  /** Where the reference starts and ends in the text, its sheet included. */
  readonly start: number;
  readonly end: number;
};

const SHEET = String.raw`(?:'((?:[^']|'')+)'|([A-Za-z_À-￿][\w.À-￿]*))!`;
const CELL = String.raw`\$?([A-Z]{1,3})(\$?)(\d+)?`;
const REF = new RegExp(String.raw`(?<![\w.$!'])(?:${SHEET})?${CELL}(?::\$?([A-Z]{1,3})\$?(\d+)?)?(?![\w(!])`, 'g');

/** The parts of a formula outside double-quoted text, each with where it starts. */
const outsideQuotes = (text: string): { readonly part: string; readonly at: number }[] => {
  const parts: { part: string; at: number }[] = [];
  let at = 0;
  let open = -1;
  for (let i = 0; i < text.length; i += 1) {
    if (text[i] !== '"') continue;
    if (open < 0) {
      parts.push({ part: text.slice(at, i), at });
      open = i;
    } else if (text[i + 1] === '"') i += 1;
    else {
      open = -1;
      at = i + 1;
    }
  }
  if (open < 0) parts.push({ part: text.slice(at), at });
  return parts;
};

/** Every reference a formula makes, in order. */
export const sheetRefs = (formula: string): SheetRef[] => {
  const refs: SheetRef[] = [];
  for (const { part, at } of outsideQuotes(formula)) {
    for (const match of part.matchAll(REF)) {
      const [whole, quoted, bare, column, dollar, row, toColumn, toRow] = match;
      const sheet = quoted !== undefined ? quoted.replace(/''/g, "'") : bare;
      // A bare word of capitals with no row and no range is a reference only in a row-free pattern; `TRUE` is never one (it is four letters).
      refs.push({
        ...(sheet === undefined ? {} : { sheet }),
        column: column!,
        ...(row === undefined ? {} : { row: Number(row) }),
        fixedRow: dollar === '$',
        ...(toColumn === undefined ? {} : { to: { column: toColumn, ...(toRow === undefined ? {} : { row: Number(toRow) }) } }),
        start: at + match.index,
        end: at + match.index + whole.length,
      });
    }
  }
  return refs;
};

/**
 * A formula as its column's pattern: each reference to a cell of its own row
 * on its own sheet loses its row (`H2+I2` on row 2 is `H+I`), so the same
 * formula down a column reads the same on every row. A reference to another
 * row, a fixed row or another sheet stays as written. A leading `=` is dropped.
 */
export const formulaPattern = (formula: string, row: number): string => {
  const text = formula.startsWith('=') ? formula.slice(1) : formula;
  let out = '';
  let at = 0;
  for (const ref of sheetRefs(text)) {
    if (ref.sheet !== undefined || ref.to !== undefined || ref.fixedRow || ref.row !== row) continue;
    out += `${text.slice(at, ref.start)}${ref.column}`;
    at = ref.end;
  }
  return out + text.slice(at);
};

/** A reference as `reads` names it: `H` on its own sheet, `Data!J` on another. */
export const refName = (ref: Pick<SheetRef, 'sheet' | 'column'>): string => (ref.sheet === undefined ? ref.column : `${ref.sheet}!${ref.column}`);

/**
 * The columns a formula reads, each once, in order: `H` for one of its own
 * sheet, `Data!J` for one of another. A range names both its ends' columns
 * and every column between them.
 */
export const formulaReads = (formula: string): string[] => {
  const reads = new Set<string>();
  for (const ref of sheetRefs(formula)) {
    const from = columnIndex(ref.column);
    const to = ref.to === undefined ? from : columnIndex(ref.to.column);
    for (let c = Math.min(from, to); c <= Math.max(from, to) && c - Math.min(from, to) < 64; c += 1) reads.add(refName({ sheet: ref.sheet, column: columnLetters(c) }));
  }
  return [...reads];
};

/** A column's zero-based index from its letters: `A` is 0, `AA` is 26. */
export const columnIndex = (letters: string): number => {
  let index = 0;
  for (const letter of letters.toUpperCase()) index = index * 26 + (letter.charCodeAt(0) - 64);
  return index - 1;
};

/** A zero-based column index as its letters: 0 is `A`, 26 is `AA`. */
export const columnLetters = (index: number): string => {
  let letters = '';
  let n = index + 1;
  while (n > 0) {
    const rest = (n - 1) % 26;
    letters = String.fromCharCode(65 + rest) + letters;
    n = Math.floor((n - 1) / 26);
  }
  return letters;
};
